import React from 'react';
import { View } from 'react-native';

import { PersonChip } from '@/components/common/person-chip';
import { Text, useTheme } from '@/components/ui';

export interface OwnerHeaderProps {
	associationName?: string | null;
	managerId?: string | null;
	managerName?: string | null;
	managerColor?: string | null;
	/**
	 * Right-aligned extras — the inbox row puts the assignee and the UNFILED badge here.
	 *
	 * Given the free space as a group, which is why the row no longer needs the
	 * conditional `marginLeft` dance it used to: two elements each claiming `auto`
	 * splits the space between them and strands the first in the middle of the row.
	 */
	trailing?: React.ReactNode;
	testID?: string;
}

/**
 * "Whose is this?" — association in bold, then the property manager, above everything
 * else on a row.
 *
 * First on purpose: on a shared queue the question asked of every row is "is this mine?",
 * and answering it below the subject means reading the row twice.
 *
 * Shared by the inbox list, the improvement-request list and the reading pane. Those
 * three had grown byte-identical copies down to the style objects, and the moment one of
 * them gains a field the others silently stop matching — which is exactly what a person
 * scanning two lists side by side would notice first.
 *
 * Renders nothing when there is nothing to say AND nothing trailing, so callers do not
 * repeat that conditional either.
 */
export const OwnerHeader: React.FC<OwnerHeaderProps> = ({
	associationName,
	managerId,
	managerName,
	managerColor,
	trailing,
	testID,
}) => {
	const theme = useTheme();
	if (!associationName && !managerName && !trailing) return null;

	return (
		<View
			testID={testID}
			style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 3 }}
		>
			{associationName ? (
				<Text
					numberOfLines={1}
					style={{
						fontSize: 12,
						fontWeight: '700',
						color: theme.colors.onSurface,
						flexShrink: 1,
						minWidth: 0,
					}}
				>
					{associationName}
				</Text>
			) : null}
			<PersonChip id={managerId} name={managerName} color={managerColor} />
			{trailing ? (
				<View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginLeft: 'auto', flexShrink: 0 }}>
					{trailing}
				</View>
			) : null}
		</View>
	);
};

export default OwnerHeader;
